import React, { useEffect, useMemo, useState } from 'react';
import { MdClose, MdUnfoldLess, MdUnfoldMore } from 'react-icons/md';
import { Dropdown } from 'lib/components';
import { Divider } from 'app/pages/admin/index.styled';
import { components } from 'react-select';
import { BiChevronDown } from 'react-icons/bi';
import { DropdownLabelWrapper, LabelText } from 'lib/components/folders/style';
import { ParagraphNormal } from 'lib/components/styles/typography';
import { FolderAccessSettings, LabelOption } from './types';
import { ItemContainer, ItemDetails } from './style';
import { useTheme } from 'styled-components/macro';
import { Flex } from 'lib/components/styles/layout';
import { Button } from 'react-covideo-common';
import { getTargetName, isMatchingTargetId } from 'lib/utils/functions';

export type Props = {
  item: FolderAccessSettings;
  accessOptionsData: FolderAccessSettings[];
  isLastItem?: boolean;
  setAccessData: React.Dispatch<React.SetStateAction<FolderAccessSettings[]>>;
};

export const CardManageAccess = ({
  item,
  accessOptionsData,
  isLastItem,
  setAccessData,
}: Props) => {
  const [showMore, setShowMore] = useState<boolean>(false);
  const [selectedAccess, setSelectedAccess] = useState<LabelOption>({
    value: '0',
    label: (<></>) as React.JSX.Element,
  });

  const themes = useTheme();
  const isToggleAllowed = (item?.children?.length ?? 0) > 0;

  const handleToggle: () => void = () => {
    if (isToggleAllowed) {
      setShowMore(prev => !prev);
    }
  };

  const DropdownIndicator = (props: any) => {
    if (!!item.isReadOnly) {
      return <></>;
    }
    return (
      components.DropdownIndicator && (
        <components.DropdownIndicator {...props}>
          <BiChevronDown />
        </components.DropdownIndicator>
      )
    );
  };

  const dropdownAccessData = useMemo(() => {
    const isReadOnly = item?.isReadOnly === true;

    return accessOptionsData.map((item: FolderAccessSettings) => ({
      value: item.id?.toString(),
      label: (
        <DropdownLabelWrapper>
          <LabelText
            color={
              isReadOnly ? themes.colors.neutral[40] : themes.colors.black[100]
            }
          >
            {item.name}
          </LabelText>
        </DropdownLabelWrapper>
      ),
    }));
  }, [accessOptionsData]);

  useEffect(() => {
    const matched = dropdownAccessData.find(
      (option: { value: string | undefined; label: React.JSX.Element }) =>
        option.value === item.access
    );
    if (matched) {
      setSelectedAccess({
        value: matched.value ?? '',
        label: matched.label,
      });
    }
  }, [item.access, dropdownAccessData]);

  const handleOnChange = (value: LabelOption) => {
    setSelectedAccess(value);
    setAccessData((prev: FolderAccessSettings[]) => {
      return prev.map(data => {
        if (isMatchingTargetId(data, item)) {
          return { ...data, access: value.value };
        }
        return data;
      });
    });
  };

  const handleRemoveItem = () => {
    setAccessData((prev: FolderAccessSettings[]) => {
      return prev.map(data => {
        if (isMatchingTargetId(data, item)) {
          return { ...data, isDeleted: true };
        }
        return data;
      });
    });
  };

  return (
    <>
      <ItemContainer>
        <Flex
          flexDirection='row'
          backgroundColor={themes.colors.white[100]}
          height='36px'
          alignItems='center'
          justifyContent='space-between'
          padding='16px 6px 16px 16px'
        >
          <Flex alignItems='center' gap='8px' width=''>
            <Flex
              cursor={isToggleAllowed ? 'pointer' : 'default'}
              alignItems='center'
              gap='8px'
              onClick={handleToggle}
              flexDirection='row'
            >
              {showMore ? (
                <MdUnfoldLess
                  fontSize={24}
                  color={themes.colors.secondary[60]}
                />
              ) : (
                <MdUnfoldMore
                  fontSize={24}
                  color={themes.colors.secondary[60]}
                />
              )}
              <ParagraphNormal>{getTargetName(item)}</ParagraphNormal>
            </Flex>
          </Flex>
          <Flex alignItems='center' gap='8px' flexDirection='row' width=''>
            <Dropdown
              disabled={!!item.isReadOnly}
              extendStyles={{
                control: {
                  borderStyle: 'hidden',
                  backgroundColor: themes.colors.white[100],
                },
                valueContainer: {
                  padding: 0,
                  marginRight: '-10px',
                },
                menu: {
                  width: '170px',
                  right: '0px',
                },
                container: {
                  margin: '0 20px 0 0',
                },
              }}
              customComponents={{
                IndicatorSeparator: () => null,
                DropdownIndicator: DropdownIndicator,
              }}
              height={40}
              creatable={false}
              className='dropdown'
              placeholder={'Select Option'}
              zIndexProp={1}
              value={selectedAccess}
              onChange={value => handleOnChange(value)}
              options={dropdownAccessData}
            />
            {!item.isReadOnly && (
              <Button
                style={{
                  backgroundColor: 'transparent',
                  border: 'none',
                  color: themes.colors.black[100],
                }}
                icon={<MdClose size='18' />}
                onClick={() => {
                  handleRemoveItem();
                }}
              />
            )}
          </Flex>
        </Flex>
        {(!isLastItem || (!!isLastItem && showMore)) && (
          <Divider marginTop='2px' maxWidth='798px' marginBottom='2px' />
        )}
        <ItemDetails show={showMore}>
          {item.children?.map((option: FolderAccessSettings) => (
            <div style={{ padding: '6px' }} key={`${option.id}=${option.name}`}>
              <ParagraphNormal>{option.name}</ParagraphNormal>
            </div>
          ))}
        </ItemDetails>
        {!!showMore && (
          <Divider marginTop='2px' maxWidth='798px' marginBottom='2px' />
        )}
      </ItemContainer>
    </>
  );
};
